import type { ReactNode } from "react";
import {
  ActivityIndicator,
  Pressable,
  Text,
  View,
  type StyleProp,
  type ViewStyle,
} from "react-native";
import { semanticColors, typography } from "../theme/tokens";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";

/**
 * `tonal` is the everyday secondary. `destructive` is the confirm button of a
 * delete or remove confirmation, beside a tonal Cancel.
 */
export type SecondaryButtonVariant = "tonal" | "destructive";

const VARIANT_COLORS: Record<
  SecondaryButtonVariant,
  { background: string; pressedBackground: string; foreground: string }
> = {
  tonal: {
    background: semanticColors.action.tonal.background,
    pressedBackground: semanticColors.action.tonal.background,
    foreground: semanticColors.action.tonal.foreground,
  },
  destructive: semanticColors.action.destructive,
};

export type SecondaryButtonProps = {
  label: string;
  onPress: () => void;
  accessibilityLabel?: string;
  /** Stable analytics identifier, required when `label` is translated. */
  action?: string;
  variant?: SecondaryButtonVariant;
  disabled?: boolean;
  /** Shows a spinner in place of the label and ignores presses. */
  loading?: boolean;
  leading?: ReactNode;
  trailing?: ReactNode;
  testID?: string;
  /** Set when used inside a row with sibling buttons. Omit for standalone (full-width). */
  flex?: number;
  style?: StyleProp<ViewStyle>;
};

export function SecondaryButton({
  label,
  onPress,
  action,
  accessibilityLabel,
  variant = "tonal",
  disabled = false,
  loading = false,
  leading,
  trailing,
  testID,
  flex,
  style,
}: SecondaryButtonProps) {
  const capturePress = usePressCapture("SecondaryButton");
  const colors = VARIANT_COLORS[variant];
  const isInactive = disabled || loading;
  return (
    <Pressable
      onPress={() => {
        if (isInactive) return;
        capturePress(pressLabel(label, accessibilityLabel), action);
        onPress();
      }}
      disabled={isInactive}
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel ?? label}
      accessibilityState={{ disabled: isInactive, busy: loading }}
      testID={testID}
      style={({ pressed }) => [
        {
          ...(flex !== undefined && { flex }),
          minHeight: 48,
          paddingVertical: 14,
          borderRadius: 999,
          backgroundColor: pressed
            ? colors.pressedBackground
            : colors.background,
          alignItems: "center",
          justifyContent: "center",
          opacity: disabled ? 0.55 : 1,
        },
        style,
      ]}
    >
      {/* Side padding sits on the label row, not the button: padding on a
          flex={1} button is added before the row splits its width, so the
          secondary would come out wider than the primary beside it. */}
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          gap: 8,
          paddingHorizontal: 24,
        }}
      >
        {loading ? (
          <ActivityIndicator size="small" color={colors.foreground} />
        ) : (
          <>
            {leading}
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 15,
                fontWeight: "500",
                fontFamily: typography.families.sans,
                color: colors.foreground,
              }}
            >
              {label}
            </Text>
            {trailing}
          </>
        )}
      </View>
    </Pressable>
  );
}

export default SecondaryButton;
